import * as Sentry from '@sentry/react'
import React from 'react'
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router'
import { ENVIRONMENT } from './environment.config'
import {
  isClientNoise,
  redactSensitiveQueryParams,
  THIRD_PARTY_NOISE_PATTERNS,
} from './sentry.config.helpers'

// Categories whose breadcrumb `data` carries a navigation/request url
// (`to`/`from` for navigation, `url` for fetch/xhr). Other categories
// have no url to scrub, so we leave them untouched.
const URL_BEARING_BREADCRUMB_CATEGORIES = ['navigation', 'fetch', 'xhr']

// Redact url-bearing fields on a breadcrumb's data without mutating it.
// Only string values are touched, so non-string data is preserved.
const redactBreadcrumbData = (data: { [key: string]: any }) => ({
  ...data,
  ...(typeof data.to === 'string' && {
    to: redactSensitiveQueryParams(data.to),
  }),
  ...(typeof data.from === 'string' && {
    from: redactSensitiveQueryParams(data.from),
  }),
  ...(typeof data.url === 'string' && {
    url: redactSensitiveQueryParams(data.url),
  }),
})

// Shared scrub for error + transaction events: redact the request url
// and the transaction name in place of the raw token. Generic over the
// structural fields we touch (not the named Sentry event types, which
// aren't all re-exported), so it stays assignable to both `beforeSend`
// (ErrorEvent) and `beforeSendTransaction` (TransactionEvent).
const redactEventUrls = <
  T extends { request?: { url?: string }; transaction?: string },
>(
  event: T,
): T => ({
  ...event,
  ...(event.request?.url && {
    request: {
      ...event.request,
      url: redactSensitiveQueryParams(event.request.url),
    },
  }),
  ...(event.transaction && {
    transaction: redactSensitiveQueryParams(event.transaction),
  }),
})

export const SENTRY_CONFIG: Sentry.BrowserOptions = {
  dsn: ENVIRONMENT.SENTRY_DSN,
  // Local dev shares the staging project but stays silent by default — set
  // VITE_SENTRY_ENABLE_LOCAL=true to emit while testing. Deployed envs send.
  enabled:
    !import.meta.env.DEV ||
    import.meta.env.VITE_SENTRY_ENABLE_LOCAL === 'true',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      createRoutesFromChildren: createRoutesFromChildren,
      useLocation: useLocation,
      useNavigationType: useNavigationType,
      matchRoutes: matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: true,
    }),
    // Capture console.warn/console.error in the browser as Sentry logs.
    Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
  ],
  // Sentry Logs: surface browser warnings/errors as structured logs,
  // correlated with the page's trace.
  enableLogs: true,
  // Fraction of page loads traced. Full in dev; 0.1 in prod to keep the
  // performance quota (and cost) bounded — raise temporarily to debug latency.
  tracesSampleRate: import.meta.env.DEV ? 1 : 0.1,
  // Session Replay is Sentry's priciest feature, so DON'T record every session:
  // 0% of normal sessions + 100% of sessions that hit an error — you get a
  // replay exactly when something broke, at near-zero quota cost.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  environment: import.meta.env.MODE,
  // Injected at build time from the git commit (see vite.config.ts) so
  // frontend issues in Sentry are attributed to a specific deploy.
  release: import.meta.env.VITE_SENTRY_RELEASE,
  attachStacktrace: true,
  // Errors thrown by injected third-party scripts, not our code.
  ignoreErrors: THIRD_PARTY_NOISE_PATTERNS,
  debug: false,
  maxBreadcrumbs: 50,
  // Strip bearer-credential query params (the waitlist magic-link `wlt`,
  // any `token`) from navigation/fetch/xhr breadcrumb urls before they
  // are stored. The SDK auto-captures full navigation urls, so without
  // this the live token would ship to Sentry and be replayable.
  beforeBreadcrumb: (breadcrumb) => {
    const isUrlBearing = URL_BEARING_BREADCRUMB_CATEGORIES.includes(
      breadcrumb.category ?? '',
    )
    if (!isUrlBearing || !breadcrumb.data) return breadcrumb
    return { ...breadcrumb, data: redactBreadcrumbData(breadcrumb.data) }
  },
  // Same scrub for the error event's request url and transaction name —
  // the route pattern is parameterised, but the raw url can still carry
  // the token, and `transaction` may hold the full path.
  // Client connectivity — a dropped request, an aborted one, Firebase
  // unable to reach Identity Platform, WebKit dropping its IndexedDB
  // connection — is the browser's condition, not ours. Errors that got
  // a response are real and kept.
  beforeSend: (event, hint) =>
    isClientNoise(hint.originalException) ? null : redactEventUrls(event),
  // Transactions are sent through a separate hook; scrub them too so the
  // token can't leak via a performance/tracing event.
  beforeSendTransaction: redactEventUrls,
}
